// Pure pricing math used by the subscription pages. No React, no API calls,
// no component state - just the slab-interpolation port of the server's
// pricing engine. Kept separate so it can be tested/debugged in isolation
// from the (much bigger) Subscription component.

const EDITION_ORDER = ["Standard", "Professional", "Enterprise"];

export const sortEditions = (editions) =>
  [...editions].sort((a, b) => {
    const ia = EDITION_ORDER.indexOf(a.editionName);
    const ib = EDITION_ORDER.indexOf(b.editionName);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return 0;
  });

// Port of the server's computeDeviceSlabPrice/interpolatePrice: unit price
// isn't a flat per-bracket rate, it's linearly interpolated between the
// lower slab's max-boundary price and the upper slab's max-boundary price,
// then multiplied by count.
//
// lower = the last slab (in list order) whose maxKey <= count
// upper = the first slab whose [minKey, maxKey] contains count
// When count sits exactly on a slab's own max boundary, lower and upper end
// up being the same slab, x1 === x2, and interpolation collapses to that
// slab's own unit price - same short-circuit the server takes, just
// reached the same way instead of a separate branch.
const findSlabBounds = (slabs, count, minKey, maxKey) => {
  let lower = null;
  let upper = null;
  for (const slab of slabs) {
    if (slab[maxKey] <= count) lower = slab;
    if (!upper && slab[minKey] <= count && slab[maxKey] >= count) upper = slab;
  }
  // Count outside the table entirely (shouldn't happen given the 50-10000
  // device / 1-50 technician input clamps) - clamp to the nearest edge slab.
  if (!lower) lower = slabs[0];
  if (!upper) upper = slabs[slabs.length - 1];
  return { lower, upper };
};

const interpolateUnitPrice = (count, lower, upper, maxKey, priceKey) => {
  const x1 = lower[maxKey];
  const x2 = upper[maxKey];
  const y1 = lower[priceKey];
  const y2 = upper[priceKey];
  if (x2 === x1) return y1;
  return y1 + ((count - x1) * (y2 - y1)) / (x2 - x1);
};

// Full slab-priced total for a given count: interpolate the unit price,
// round to 4dp the way the server's BigDecimal interpolatePrice does, then
// multiply by count.
export const computeSlabTotal = (count, slabs, minKey, maxKey, priceKey) => {
  if (!slabs?.length || !count) return 0;
  const { lower, upper } = findSlabBounds(slabs, count, minKey, maxKey);
  const unitPrice = interpolateUnitPrice(count, lower, upper, maxKey, priceKey);
  return Number(unitPrice.toFixed(4)) * count;
};

export const getConversionRate = (catalog, targetCurrency) => {
  if (!catalog) return 1;

  const baseCurrency = catalog.orgCurrencyCode;

  if (baseCurrency === targetCurrency) {
    return 1;
  }

  const rate = catalog.currencies.find(
    (c) =>
      c.baseCurrencyCode === "USD" &&
      c.targetCurrencyCode === targetCurrency
  );

  if (baseCurrency === "USD") {
    return rate?.conversionRate ?? 1;
  }

  const usdToBase = catalog.currencies.find(
    (c) =>
      c.baseCurrencyCode === "USD" &&
      c.targetCurrencyCode === baseCurrency
  )?.conversionRate ?? 1;

  const usdToTarget = rate?.conversionRate ?? 1;

  return usdToTarget / usdToBase;
};